import { createSlice } from '@reduxjs/toolkit';

const DEFAULT_SERVERS = [
  { name: 'Local', url: 'http://localhost:8000' },
  // { name: 'Remote', url: 'https://yv7zcavavk.execute-api.us-east-1.amazonaws.com/dev' },
  // { name: 'JH', url: 'https://u77fhty4fb.execute-api.us-east-1.amazonaws.com/jh2' },
];

const networkUndoSlice = createSlice({
  name: 'networkUndo',
  initialState: {
    meta: {},
    nodes: [],
    edges: [],
    servers: DEFAULT_SERVERS,
    selectedServer: 'http://localhost:8000',
  },
  reducers: {
    setNetwork: (state, action) => {
      state.meta = action.payload.meta;
      state.nodes = action.payload.nodes;
      state.edges = action.payload.edges;
    },
    setNodes: (state, action) => {
      state.nodes = action.payload;
    },
    setEdges: (state, action) => {
      state.edges = action.payload;
    },
    addNode: (state, action) => {
      state.nodes.push(action.payload);
    },
    addEdge: (state, action) => {
      const newEdge = action.payload;

      // curve edges (slight offset edges between the same nodes)
      const toFrom = [newEdge.to, newEdge.from];
      const adjacentEdges = state.edges.filter(
        (edge) => toFrom.includes(edge.to) && toFrom.includes(edge.from) && edge.to !== edge.from,
      );
      if (adjacentEdges.length > 0) {
        const curved = { type: 'curvedCCW', roundness: 0.075 };
        newEdge.smooth = curved;
        const index = state.edges.findIndex((item) => item.id === adjacentEdges[0].id);
        state.edges[index].smooth = curved;
      } else {
        const smooth = { type: 'straight', roundness: 0 };
        newEdge.smooth = smooth;
      }

      state.edges.push(newEdge);
    },
    updateNode: (state, action) => {
      const node = action.payload;
      if ('oldId' in node) {
        // add new (duplicate) node
        state.nodes.push(node);

        // update edges (point to new node)
        state.edges.forEach((edge, index) => {
          if (edge.to === node.oldId) state.edges[index].to = node.id;
          if (edge.from === node.oldId) state.edges[index].from = node.id;
        });

        // remove old node
        const index = state.nodes.findIndex((item) => item.id === node.oldId);
        state.nodes.splice(index, 1);

        // clean up
        delete node.oldId;
      } else {
        const index = state.nodes.findIndex((item) => item.id === node.id);
        state.nodes[index] = { ...state.nodes[index], ...node };
      }
    },
    updateEdge: (state, action) => {
      const edge = action.payload;
      const index = state.edges.findIndex((item) => item.id === edge.id);
      state.edges[index] = { ...state.edges[index], ...edge };
    },
    removeNode: (state, action) => {
      const id = action.payload;
      const index = state.nodes.findIndex((item) => item.id === id);
      if (index > -1) {
        state.nodes.splice(index, 1);
      }
      // remove orphaned edges
      state.edges = state.edges.filter((item) => item.to !== id && item.from !== id);
    },
    removeEdge: (state, action) => {
      const id = action.payload;
      const index = state.edges.findIndex((item) => item.id === id);

      // remove edge
      if (index > -1) {
        // reset adjacent edges curve
        const toFrom = [state.edges[index].to, state.edges[index].from];
        state.edges.forEach((edge, edgeIndex) => {
          if (toFrom.includes(edge.to) && toFrom.includes(edge.from)) {
            state.edges[edgeIndex].smooth = { type: 'straight', roundness: 0 };
          }
        });

        state.edges.splice(index, 1);
      }
    },
    reset: (state) => {
      state.meta = {};
      state.edges = [];
      state.nodes = [];
    },
    setMeta: (state, action) => {
      state.meta = action.payload;
    },
    setNodePositions: (state, action) => {
      state.nodes.forEach((node) => {
        if (node.id in action.payload) {
          node.x = action.payload[node.id].x;
          node.y = action.payload[node.id].y;
        }
      });
    },
    addServer: (state, action) => {
      if (!state.servers) {
        state.servers = [...DEFAULT_SERVERS];
      }
      state.servers.push(action.payload);
    },
    deleteServer: (state, action) => {
      if (!state.servers) {
        state.servers = [...DEFAULT_SERVERS];
      }
      state.servers = state.servers.filter((server) => server.url !== action.payload);
      if (state.selectedServer === action.payload) {
        state.selectedServer = '';
      }
    },
    setSelectedServer: (state, action) => {
      if (!state.servers) {
        state.servers = [...DEFAULT_SERVERS];
      }
      state.selectedServer = action.payload;
    },
  },
});

export const {
  setNetwork,
  setNodes,
  setEdges,
  addNode,
  addEdge,
  updateNode,
  updateEdge,
  removeNode,
  removeEdge,
  reset,
  setMeta,
  setNodePositions,
  addServer,
  deleteServer,
  setSelectedServer,
} = networkUndoSlice.actions;
export default networkUndoSlice.reducer;
